import React, { useEffect, useState } from 'react';
import {
  Copy, ExternalLink, Globe, Lock, Send, EyeOff, AlertTriangle, ChevronUp, ChevronDown, CircleSlash,
} from 'lucide-react';
import {
  Alert, Badge, Button, Checkbox, IconButton, Spinner, TextInput, useToast,
} from '@parsina/framework';
import { C, RADIUS, soft } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import SegmentedControl from '../../../components/common/SegmentedControl';
import { useMyPageStrings } from '../myPageStrings';
import { useSlugCheck } from '../useDoctorPage';
import {
  STATUS_BADGE, statusLabelKey, publishState, everPublished, isLive, templates, templateById,
  effectiveTheme, themeForTemplate, orderedSections, moveSection, sectionTitle, normalizeSlugInput,
  slugFormatError, errorsBySection, LIMITS,
} from '../MyPage.shared';
import { useErrorText, useFieldStyle } from './common';

// ─────────────────────────────────────────────────────────────────────────────
// The builder's rail: where the page stands (status card), what it looks like
// (template + theme), where it lives (slug) and what it holds (sections).
// ─────────────────────────────────────────────────────────────────────────────

const railCard = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: RADIUS.card,
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '1rem', display: 'grid', gap: '0.75rem', minWidth: 0,
};
const railTitle = { margin: 0, fontSize: '0.875rem', fontWeight: 800, color: C.textPrimary };

// ── Status card ─────────────────────────────────────────────────────────────

export function StatusCard({ page, cards, onPreview, onPublishClick, onUnpublish, testId = 'my-page-status' }) {
  const s = useMyPageStrings();
  const toast = useToast();
  const errorText = useErrorText();
  const { formatDateTime } = useTranslation();
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const { canPublish, reasons, suspended } = publishState(page);
  const live = isLive(page);
  const previewBusy = !!cards.preview?.saving;
  const publishBusy = !!cards.publish?.saving;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(page.publicUrl);
      toast.success(s('myPage.statusCard.copied'));
    } catch {
      toast.error(s('myPage.statusCard.copyFailed'));
    }
  };

  return (
    <section style={railCard} data-testid={testId} data-status={page.status || 'unknown'}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <h2 style={{ ...railTitle, flex: 1 }}>{s('myPage.statusCard.title')}</h2>
        <Badge variant={STATUS_BADGE[page.status] || 'outline'} data-testid={`${testId}-badge`}>
          {s(statusLabelKey(page.status))}
        </Badge>
      </div>

      {page.publicUrl ? (
        <div style={{ display: 'grid', gap: '0.375rem' }}>
          <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>
            {live ? s('myPage.statusCard.liveUrl') : s('myPage.statusCard.futureUrl')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', minWidth: 0 }}>
            {live ? (
              <a
                href={page.publicUrl} target="_blank" rel="noopener noreferrer" dir="ltr"
                data-testid={`${testId}-url`}
                style={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', color: C.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', unicodeBidi: 'isolate' }}
              >
                {page.publicUrl}
              </a>
            ) : (
              <span dir="ltr" data-testid={`${testId}-url`} style={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', color: C.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', unicodeBidi: 'isolate' }}>
                {page.publicUrl}
              </span>
            )}
            {live ? (
              <IconButton
                variant="ghost" size={36} icon={<Copy size={16} aria-hidden />}
                label={s('myPage.statusCard.copy')} onClick={copy} data-testid={`${testId}-copy`}
              />
            ) : null}
          </div>
          {live && page.publishedAtMs ? (
            <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>
              {s('myPage.statusCard.lastPublished', { date: formatDateTime(Number(page.publishedAtMs)) })}
            </div>
          ) : null}
        </div>
      ) : null}

      {!suspended ? (
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          <Button
            variant="outline" onClick={onPreview} disabled={previewBusy}
            data-testid={`${testId}-preview`} className="w-full"
          >
            {previewBusy ? <Spinner size={14} /> : <ExternalLink size={16} aria-hidden />}
            {s('myPage.statusCard.preview')}
          </Button>
          <Button
            onClick={onPublishClick} disabled={!canPublish || publishBusy}
            data-testid={`${testId}-publish`} className="w-full"
            aria-describedby={!canPublish && reasons.length ? `${testId}-blockers` : undefined}
          >
            <Send size={16} aria-hidden />
            {live ? s('myPage.statusCard.republish') : s('myPage.statusCard.publish')}
          </Button>
          {live ? (
            <Button
              variant="ghost" onClick={() => setConfirmUnpublish(true)} disabled={publishBusy}
              data-testid={`${testId}-unpublish`} className="w-full"
            >
              <EyeOff size={16} aria-hidden />
              {s('myPage.statusCard.unpublish')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {cards.preview?.error ? (
        <Alert variant="error">{errorText(cards.preview.error)}</Alert>
      ) : null}

      {!canPublish && reasons.length ? (
        <div id={`${testId}-blockers`} data-testid={`${testId}-blockers`} style={{ display: 'grid', gap: '0.375rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.textPrimary }}>{s('myPage.statusCard.blockersTitle')}</div>
          <ul style={{ margin: 0, paddingInlineStart: '1.1rem', display: 'grid', gap: '0.25rem', fontSize: '0.75rem', color: C.textSecondary }}>
            {reasons.map((r, i) => <li key={`${r.code}-${i}`} dir="auto">{r.message}</li>)}
          </ul>
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmUnpublish}
        onOpenChange={setConfirmUnpublish}
        title={s('myPage.statusCard.unpublishTitle')}
        description={s('myPage.statusCard.unpublishBody')}
        confirmLabel={s('myPage.statusCard.unpublishConfirm')}
        destructive
        loading={publishBusy}
        onConfirm={async () => { const ok = await onUnpublish(); if (ok) setConfirmUnpublish(false); }}
        confirmTestId={`${testId}-unpublish-confirm`}
      />
    </section>
  );
}

// ── Template picker ─────────────────────────────────────────────────────────

/** A CSS-only schematic of a template's first screen — no images. */
function TemplateThumb({ id }) {
  const bar = (w, h = 4, tone = 0.18) => (
    <span style={{ display: 'block', width: w, height: h, borderRadius: 2, background: soft(C.navy, tone) }} />
  );
  return (
    <span aria-hidden style={{
      display: 'grid', gap: 4, alignContent: 'start', padding: 6, height: '4.25rem',
      borderRadius: RADIUS.control, background: C.bg, border: `1px solid ${C.border}`, overflow: 'hidden',
    }}>
      {id === 'modern' ? (
        <>
          <span style={{ display: 'block', height: 18, borderRadius: 3, background: soft(C.navy, 0.35) }} />
          <span style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
            {[0, 1, 2].map((i) => <span key={i} style={{ height: 14, borderRadius: 3, background: C.card, border: `1px solid ${C.border}` }} />)}
          </span>
        </>
      ) : id === 'minimal' ? (
        <>
          {bar('55%', 6, 0.4)}
          {bar('80%')}
          {bar('70%')}
          {bar('40%', 4, 0.1)}
          {bar('75%')}
        </>
      ) : (
        <span style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <span style={{ width: 20, height: 20, borderRadius: '50%', background: soft(C.navy, 0.35), flexShrink: 0 }} />
          <span style={{ display: 'grid', gap: 3, flex: 1 }}>
            {bar('70%', 5, 0.35)}
            {bar('90%')}
            {bar('60%')}
          </span>
        </span>
      )}
    </span>
  );
}

export function TemplatePicker({ page, cards, onChange, readOnly, testId = 'my-page-template' }) {
  const s = useMyPageStrings();
  const busy = !!cards.template?.saving;
  return (
    <section style={railCard} data-testid={testId}>
      <h2 style={railTitle}>{s('myPage.template.title')}</h2>
      <div role="radiogroup" aria-label={s('myPage.template.title')} style={{ display: 'grid', gap: '0.5rem' }}>
        {templates().map((tpl) => {
          const active = tpl.id === templateById(page.templateId).id;
          return (
            <Button
              key={tpl.id}
              variant="ghost"
              role="radio"
              aria-checked={active}
              disabled={readOnly || busy}
              onClick={() => { if (!active) onChange(tpl.id); }}
              data-testid={`${testId}-${tpl.id}`}
              className="h-auto w-full justify-start rounded-xl p-2 font-normal"
              style={{
                display: 'grid', gridTemplateColumns: '5rem minmax(0,1fr)', gap: '0.75rem', textAlign: 'start',
                border: `1.5px solid ${active ? C.navy : C.border}`,
                background: active ? soft(C.navy, 0.05) : C.card,
              }}
            >
              <TemplateThumb id={tpl.id} />
              <span style={{ display: 'grid', gap: '0.25rem', minWidth: 0, whiteSpace: 'normal' }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>{s(`myPage.templates.${tpl.id}.name`)}</span>
                <span style={{ fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.6 }}>{s(`myPage.templates.${tpl.id}.description`)}</span>
              </span>
            </Button>
          );
        })}
      </div>
      {busy ? <div role="status" style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.save.saving')}</div> : null}
    </section>
  );
}

/** The payload a template switch sends: the template + a theme it offers. */
export const templateSwitchInput = (page, templateId) => ({
  templateId, theme: themeForTemplate(templateId, page.theme),
});

// ── Theme ───────────────────────────────────────────────────────────────────

export function ThemeCard({ page, cards, onChange, readOnly, testId = 'my-page-theme' }) {
  const s = useMyPageStrings();
  const tpl = templateById(page.templateId);
  const theme = effectiveTheme(page.templateId, page.theme);
  const busy = !!cards.theme?.saving;
  return (
    <section style={railCard} data-testid={testId}>
      <h2 style={railTitle}>{s('myPage.theme.title')}</h2>
      <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.theme.palette')}</div>
      <div role="radiogroup" aria-label={s('myPage.theme.palette')} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {tpl.palettes.map((p) => {
          const active = p.id === theme.palette;
          const name = s(`myPage.palettes.${p.id}`);
          return (
            <Button
              key={p.id}
              variant="ghost"
              role="radio"
              aria-checked={active}
              aria-label={name}
              title={name}
              disabled={readOnly || busy}
              onClick={() => { if (!active) onChange({ ...theme, palette: p.id }); }}
              data-testid={`${testId}-palette-${p.id}`}
              className="h-11 w-11 rounded-full p-0"
              style={{ border: `2px solid ${active ? C.textPrimary : C.border}`, padding: 3 }}
            >
              {/* The palette's own colours are DATA from the manifest (a named
                  exception in DESIGN_SYSTEM §15), shown so the doctor can pick. */}
              <span aria-hidden style={{
                display: 'block', width: '100%', height: '100%', borderRadius: '50%',
                background: `linear-gradient(135deg, ${p.primary} 0 55%, ${p.accent} 55% 100%)`,
              }} />
            </Button>
          );
        })}
      </div>
      <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.theme.density')}</div>
      <SegmentedControl
        size="sm"
        ariaLabel={s('myPage.theme.density')}
        testId={`${testId}-density`}
        value={theme.density}
        onChange={(d) => { if (!readOnly && !busy && d !== theme.density) onChange({ ...theme, density: d }); }}
        items={tpl.densities.map((d) => ({ id: d, label: s(`myPage.theme.densities.${d}`) }))}
      />
      <div style={{ fontSize: '0.6875rem', color: C.textMuted }}>{s('myPage.theme.onlyTemplate')}</div>
    </section>
  );
}

// ── Slug ────────────────────────────────────────────────────────────────────

export function SlugField({ page, cards, onSave, readOnly, testId = 'my-page-slug' }) {
  const s = useMyPageStrings();
  const errorText = useErrorText();
  const fieldStyle = useFieldStyle();
  const [value, setValue] = useState(page.slug || '');
  useEffect(() => { setValue(page.slug || ''); }, [page.slug]);
  const normalized = normalizeSlugInput(value);
  const formatError = slugFormatError(normalized);
  const changed = normalized !== (page.slug || '');
  const { checking, result } = useSlugCheck(normalized, { enabled: !page.slugLocked && changed && !formatError });
  const busy = !!cards.slug?.saving;

  if (page.slugLocked) {
    return (
      <section style={railCard} data-testid={testId} data-state="locked">
        <h2 style={railTitle}>{s('myPage.slug.label')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: C.textPrimary }}>
          <Lock size={16} aria-hidden style={{ color: C.textSecondary, flexShrink: 0 }} />
          <span dir="ltr" style={{ unicodeBidi: 'isolate', overflow: 'hidden', textOverflow: 'ellipsis' }}>{page.slug}</span>
        </div>
        <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{s('myPage.slug.lockedHint')}</div>
      </section>
    );
  }

  const availabilityError = changed && !formatError && result && !result.available
    ? (result.message || s('myPage.slug.taken')) : '';
  const fieldError = changed && formatError ? s(formatError) : availabilityError;
  const canSave = changed && !formatError && !checking && result?.available && !busy && !readOnly;

  return (
    <section style={railCard} data-testid={testId} data-state="editable">
      <h2 style={railTitle}>{s('myPage.slug.label')}</h2>
      <TextInput
        dir="ltr"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        maxLength={LIMITS.slug_max}
        disabled={readOnly}
        error={fieldError}
        helperText={!fieldError ? s('myPage.slug.hint') : undefined}
        aria-label={s('myPage.slug.label')}
        inputStyle={fieldStyle}
        data-testid={`${testId}-input`}
        autoComplete="off"
      />
      {changed && !formatError ? (
        <div role="status" aria-live="polite" style={{ fontSize: '0.75rem', color: result?.available ? C.green : C.textSecondary }}>
          {checking ? s('myPage.slug.checking') : result?.available ? s('myPage.slug.available') : null}
        </div>
      ) : null}
      {cards.slug?.error ? <Alert variant="error">{errorText(cards.slug.error)}</Alert> : null}
      <Button size="sm" disabled={!canSave} onClick={() => onSave(normalized)} data-testid={`${testId}-save`}>
        {busy ? <Spinner size={14} /> : null}
        {s('myPage.slug.save')}
      </Button>
    </section>
  );
}

// ── Section list ────────────────────────────────────────────────────────────

export function SectionList({
  page, cards, selected, onSelect, onToggle, onMove, readOnly, publishErrors, testId = 'my-page-sections',
}) {
  const s = useMyPageStrings();
  const sections = orderedSections(page.sections);
  const errorsByKind = errorsBySection([...(page.publishBlockers || []), ...(publishErrors || [])]);
  const busy = !!cards.sections?.saving;
  const errorText = useErrorText();

  const onKeyDown = (e, kind) => {
    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    e.preventDefault();
    const kinds = moveSection(sections, kind, e.key === 'ArrowUp' ? -1 : 1);
    if (kinds && !readOnly && !busy) onMove(kinds);
  };

  return (
    <section style={railCard} data-testid={testId}>
      <h2 style={railTitle}>{s('myPage.sections.title')}</h2>
      <div style={{ fontSize: '0.6875rem', color: C.textMuted }}>{s('myPage.sections.reorderHint')}</div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.25rem' }}>
        {sections.map((sec, i) => {
          const active = selected === sec.kind;
          const hasErrors = !!errorsByKind[sec.kind]?.length;
          const toggleCard = cards[`toggle-${sec.kind}`];
          return (
            <li
              key={sec.kind}
              data-testid={`${testId}-${sec.kind}`}
              data-enabled={sec.enabled}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.25rem',
                borderRadius: RADIUS.control, background: active ? soft(C.navy, 0.07) : 'transparent',
              }}
            >
              <Button
                variant="ghost"
                aria-current={active ? 'true' : undefined}
                onClick={() => onSelect(sec.kind)}
                onKeyDown={(e) => onKeyDown(e, sec.kind)}
                data-testid={`${testId}-${sec.kind}-open`}
                className="h-auto min-h-11 flex-1 justify-start rounded-lg px-2 py-1.5 font-normal"
                style={{ minWidth: 0, textAlign: 'start', whiteSpace: 'normal' }}
              >
                <span style={{ display: 'grid', gap: '0.125rem', minWidth: 0 }}>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: sec.enabled ? C.textPrimary : C.textSecondary, display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                    {s(`myPage.sections.kinds.${sec.kind}`)}
                    {hasErrors ? (
                      <AlertTriangle size={14} aria-label={s('myPage.sections.hasErrors')} style={{ color: C.amber, flexShrink: 0 }} />
                    ) : null}
                  </span>
                  {!sec.supportedByTemplate ? (
                    <span style={{ fontSize: '0.6875rem', color: C.textSecondary, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                      <CircleSlash size={12} aria-hidden /> {s('myPage.sections.unsupported')}
                    </span>
                  ) : sec.titleOverride ? (
                    <span dir="auto" style={{ fontSize: '0.6875rem', color: C.textSecondary }}>{sectionTitle(sec)}</span>
                  ) : null}
                </span>
              </Button>
              <Checkbox
                checked={sec.enabled}
                disabled={readOnly || sec.required || !!toggleCard?.saving}
                onChange={(v) => onToggle(sec.kind, v === true)}
                label={<span className="sr-only">{s('myPage.sections.show', { name: s(`myPage.sections.kinds.${sec.kind}`) })}</span>}
                data-testid={`${testId}-${sec.kind}-toggle`}
                title={sec.required ? s('myPage.sections.required') : undefined}
              />
              <IconButton
                variant="ghost" size={32} icon={<ChevronUp size={15} aria-hidden />}
                label={s('myPage.sections.moveUp', { name: s(`myPage.sections.kinds.${sec.kind}`) })}
                disabled={readOnly || busy || i === 0}
                onClick={() => { const k = moveSection(sections, sec.kind, -1); if (k) onMove(k); }}
                data-testid={`${testId}-${sec.kind}-up`}
              />
              <IconButton
                variant="ghost" size={32} icon={<ChevronDown size={15} aria-hidden />}
                label={s('myPage.sections.moveDown', { name: s(`myPage.sections.kinds.${sec.kind}`) })}
                disabled={readOnly || busy || i === sections.length - 1}
                onClick={() => { const k = moveSection(sections, sec.kind, 1); if (k) onMove(k); }}
                data-testid={`${testId}-${sec.kind}-down`}
              />
            </li>
          );
        })}
      </ol>
      {cards.sections?.error ? <Alert variant="error">{errorText(cards.sections.error)}</Alert> : null}
      {/* A show/hide that failed must say so: otherwise the box just un-ticks itself. */}
      {sections.filter((sec) => cards[`toggle-${sec.kind}`]?.error).map((sec) => (
        <Alert key={sec.kind} variant="error" data-testid={`${testId}-${sec.kind}-toggle-error`}>
          {s('myPage.sections.toggleFailed', { name: s(`myPage.sections.kinds.${sec.kind}`) })}{' '}
          {errorText(cards[`toggle-${sec.kind}`].error)}
        </Alert>
      ))}
    </section>
  );
}

// ── Banners (zone ②) ────────────────────────────────────────────────────────

export function PageBanners({ page, testId = 'my-page-banner' }) {
  const s = useMyPageStrings();
  if (page.status === 'suspended') {
    return (
      <Alert variant="error" icon={<AlertTriangle size={16} aria-hidden />} title={s('myPage.banner.suspendedTitle')} data-testid={`${testId}-suspended`}>
        <div dir="auto">{page.suspendedReason || s('myPage.banner.suspendedNoReason')}</div>
        <div style={{ marginTop: '0.25rem' }}>{s('myPage.banner.suspendedSupport')}</div>
      </Alert>
    );
  }
  if (page.hasUnpublishedChanges && everPublished(page)) {
    return (
      <Alert variant="warning" icon={<Globe size={16} aria-hidden />} data-testid={`${testId}-unpublished`}>
        {s('myPage.banner.unpublishedChanges')}
      </Alert>
    );
  }
  return null;
}
